/**
 * useGetStartedChecklist Hook
 *
 * Main hook for the progressive onboarding checklist.
 * Reads completion state from company.payment flags, provides real-time updates,
 * and exposes mutations to mark the checklist as triggered/completed.
 */

import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useMutation, useApolloClient } from '@apollo/client/react';
import { UPDATE_GET_STARTED, GET_COMPANY_INFO } from '@scio3/graphql-client';
import { analyticsGetStartedStep, analyticsGetStartedCompleted } from '@/lib/analytics-service';
import type { UpdateGetStartedResponse } from '@scio3/graphql-client';
import type { CompanyPayment, CompanyGetStarted, CompanyInfo } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { CHECKLIST_STEPS, STEP_FLAG_MAPPING, isFlagCompleted } from '../constants';
import type { ChecklistState, StepState } from '../types';
import { StepId } from '../types';

/**
 * Derive step completion status from payment flags.
 * Step 1 (pairScreen) requires BOTH flags to be completed.
 */
function isStepCompleted(stepId: StepId, payment: CompanyPayment | undefined): boolean {
  if (!payment) return false;

  const flagNames = STEP_FLAG_MAPPING[stepId];
  // All flags for this step must be completed
  return flagNames.every((flagName) => {
    const value = payment[flagName] as string | undefined;
    return isFlagCompleted(value);
  });
}

/**
 * Hook for the Get Started checklist widget.
 * Returns current state, step data, and mutation handlers.
 */
export function useGetStartedChecklist() {
  const company = useAuthStore((s) => s.company);
  const setCompany = useAuthStore((s) => s.setCompany);
  const client = useApolloClient();

  // Mutation to update get-started state
  const [updateGetStarted, { loading: updateLoading }] =
    useMutation<UpdateGetStartedResponse>(UPDATE_GET_STARTED);

  // Refetch company info (for real-time updates)
  const refetchCompany = useCallback(async () => {
    try {
      const result = await client.query<{ getCompanyInfo: CompanyInfo }>({
        query: GET_COMPANY_INFO,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      if (result.data?.getCompanyInfo) {
        setCompany(result.data.getCompanyInfo);
      }
    } catch {
      // Silently fail — company info is not critical for app function
    }
  }, [client, setCompany]);

  // Subscribe to real-time company updates
  // The subscription filters by 'company' entity type
  useRealtimeSubscription('company', refetchCompany, {
    refetchOnUpdate: true, // We need to refetch on UPDATE events for company flags
  });

  // Track previously completed step IDs to detect new completions
  const prevCompletedRef = useRef<Set<StepId>>(new Set());

  // Compute checklist state from company data
  const state = useMemo((): ChecklistState => {
    const payment = company?.payment;
    const getStarted: CompanyGetStarted | undefined = company?.getStarted;

    // Build steps with completion status
    const steps: StepState[] = CHECKLIST_STEPS.map((step) => ({
      ...step,
      completed: isStepCompleted(step.id, payment),
    }));

    const completedCount = steps.filter((s) => s.completed).length;
    const totalSteps = steps.length;
    const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

    // Check if all required steps are completed
    const requiredSteps = steps.filter((s) => s.required);
    const allRequiredComplete = requiredSteps.every((s) => s.completed);

    // Widget visibility: show when triggered AND not completed
    const isTriggered = getStarted?.triggered ?? false;
    const isCompleted = getStarted?.completed ?? false;
    const isVisible = isTriggered && !isCompleted;

    return {
      isComplete: allRequiredComplete,
      isVisible,
      steps,
      completedCount,
      totalSteps,
      progressPercent,
    };
  }, [company]);

  // Track step completions via PostHog (side effect, not inside useMemo)
  useEffect(() => {
    const prevCompleted = prevCompletedRef.current;
    for (const step of state.steps) {
      if (step.completed && !prevCompleted.has(step.id)) {
        const stepIndex = CHECKLIST_STEPS.findIndex((s) => s.id === step.id);
        analyticsGetStartedStep({ stepId: step.id, stepNumber: stepIndex + 1 });
      }
    }
    const completedIds = new Set(state.steps.filter((s) => s.completed).map((s) => s.id));
    if (state.isComplete && completedIds.size > prevCompleted.size) {
      analyticsGetStartedCompleted({ stepsCompleted: state.completedCount });
    }
    prevCompletedRef.current = completedIds;
  }, [state]);

  // Mark checklist as completed (hides widget permanently)
  const completeChecklist = useCallback(async () => {
    // Optimistically hide widget immediately for responsive UX
    if (company) {
      setCompany({
        ...company,
        getStarted: {
          triggered: company.getStarted?.triggered ?? true,
          completed: true,
          completedDate: new Date().toISOString(),
        },
      });
    }
    try {
      await updateGetStarted({
        variables: {
          updateData: { getStarted: { completed: true } },
        },
      });
      // Refetch to sync server state on success
      await refetchCompany();
    } catch (err) {
      console.error('Failed to complete checklist:', err);
      // Keep optimistic state — widget stays hidden even if API fails
    }
  }, [updateGetStarted, refetchCompany, company, setCompany]);

  // Trigger the checklist (makes widget visible)
  const triggerChecklist = useCallback(async () => {
    try {
      await updateGetStarted({
        variables: {
          updateData: { getStarted: { triggered: true } },
        },
      });
      await refetchCompany();
    } catch (err) {
      console.error('Failed to trigger checklist:', err);
    }
  }, [updateGetStarted, refetchCompany]);

  return {
    /** Current checklist state */
    state,
    /** Whether a mutation is in progress */
    loading: updateLoading,
    /** Mark checklist as completed (hides widget permanently) */
    completeChecklist,
    /** Trigger the checklist (makes widget visible) */
    triggerChecklist,
    /** Manually refetch company data */
    refetch: refetchCompany,
  };
}
